import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { Users, Search, UserMinus, Loader2, CalendarDays } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { PlayerPreviewModal } from "./PlayerPreviewModal";

interface Friend {
  id: string;
  name: string;
  email: string | null;
  profileImageUrl: string | null;
  skillLevel: number | null;
}

interface FriendsListProps {
  onScheduleMatch?: (friendId: string, friendName: string) => void;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map(n => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";
}

export function FriendsList({ onScheduleMatch }: FriendsListProps = {}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [playerModalOpen, setPlayerModalOpen] = useState(false);
  const { toast } = useToast();

  const { data: friends = [], isLoading } = useQuery<Friend[]>({
    queryKey: ['/api/friends'],
  });

  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

  const removeFriendMutation = useMutation({
    mutationFn: async (friendId: string) => {
      setRemovingIds(prev => new Set(prev).add(friendId));
      await apiRequest("DELETE", `/api/friends/${friendId}`);
      return friendId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
      toast({
        title: "Amigo eliminado",
        description: "Ya no son amigos.",
      });
    },
    onError: (_, friendId) => {
      setRemovingIds(prev => {
        const next = new Set(prev);
        next.delete(friendId);
        return next;
      });
      toast({
        title: "Error",
        description: "No se pudo eliminar al amigo.",
        variant: "destructive",
      });
    },
    onSettled: (_, __, friendId) => {
      setRemovingIds(prev => {
        const next = new Set(prev);
        next.delete(friendId);
        return next;
      });
    },
  });

  const filteredFriends = friends.filter(friend => 
    friend.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (friend.email && friend.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (friends.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Aún no tienes amigos</p>
            <p className="text-sm">¡Busca jugadores para conectar!</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar entre tus amigos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          data-testid="input-search-friends"
        />
      </div>

      <div className="space-y-2">
        {filteredFriends.map((friend) => (
          <Card 
            key={friend.id} 
            className="overflow-hidden cursor-pointer hover-elevate"
            onClick={() => {
              setSelectedPlayerId(friend.id);
              setPlayerModalOpen(true);
            }}
          >
            <CardContent className="p-3">
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12 border-2 border-green-500/30">
                  <AvatarImage src={friend.profileImageUrl || undefined} alt={friend.name} />
                  <AvatarFallback className="bg-green-500/20 text-green-700 dark:text-green-400 font-semibold">
                    {getInitials(friend.name)}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate" data-testid={`text-friend-name-${friend.id}`}>
                      {friend.name}
                    </span>
                    <Badge variant="secondary" className="shrink-0 bg-green-500/20 text-green-700 dark:text-green-400 border-0 text-[10px] px-1.5 py-0">
                      Amigo
                    </Badge>
                  </div>
                  {friend.skillLevel !== null && (
                    <div className="text-xs text-muted-foreground">
                      Nivel {friend.skillLevel.toFixed(1)}
                    </div>
                  )}
                </div>

                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  {onScheduleMatch && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onScheduleMatch(friend.id, friend.name)}
                      data-testid={`button-schedule-${friend.id}`}
                    >
                      <CalendarDays className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => removeFriendMutation.mutate(friend.id)}
                    disabled={removingIds.has(friend.id)}
                    data-testid={`button-remove-friend-${friend.id}`}
                  >
                    {removingIds.has(friend.id) ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <UserMinus className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredFriends.length === 0 && searchQuery && (
          <div className="text-center py-8 text-muted-foreground">
            <p>No se encontraron amigos con "{searchQuery}"</p>
          </div>
        )}
      </div>

      <PlayerPreviewModal
        playerId={selectedPlayerId}
        open={playerModalOpen}
        onOpenChange={setPlayerModalOpen}
      />
    </div>
  );
}
